import React, { useState, useEffect } from "react";
import { fetchCancelledByCustomerOrders, deleteOrder, updateOrderStatus } from "../../../api/OrderAPIs";
import { fetchDesignById } from "../../../api/DesignAPIs";
import { fetchTypeById } from "../../../api/TypeAPIs";
import { useAuth } from "../../../utils/auth";
import { canViewOrders, canMutateOrders } from "../../../utils/roles";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_BASE_URL_V2 } from "../../../api/URL_Header";

// SVG Icons
const Icons = {
  SpamAll: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
  ),
  Delete: () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  ),
  Spam: () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  Old: () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  Changes: () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    </svg>
  ),
  CheckCircle: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  Warning: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
  ),
};

const SkeletonRow = () => (
  <div className="animate-pulse border-b border-gray-100">
    <div className="flex items-center gap-4 p-4">
      <div className="h-5 bg-gray-200 rounded w-16"></div>
      <div className="h-5 bg-gray-200 rounded w-32"></div>
      <div className="h-5 bg-gray-200 rounded w-28"></div>
      <div className="h-5 bg-gray-200 rounded w-20"></div>
      <div className="h-8 bg-gray-200 rounded w-48"></div>
      <div className="h-8 bg-gray-200 rounded w-16"></div>
    </div>
  </div>
);

const CancelledbyCustomerPage = () => {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const canAct = canMutateOrders(authState.role);
  const [loadedOrders, setLoadedOrders] = useState([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isOrdersLoading, setIsOrdersLoading] = useState(false);
  const [isSpamAllLoading, setIsSpamAllLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!canViewOrders(authState.role)) {
      navigate("/login");
      return;
    }
    loadInitialOrders();
  }, [authState, navigate]);

  const enrichOrders = async (data) => {
    // تبسيط البيانات - نأخذ فقط المعلومات المطلوبة
    return await Promise.all(
      data.map(async (order) => {
        return {
          orderNumber: order.orderId,
          total: order.totalAmount,
          name: order.name || "Unknown Customer",
          phoneNumber: order.phoneNumber || "Unknown Phone",
          phoneNumber2: order.phoneNumber2 || "",
          note: order.note || "",
          status: order.status || "Cancelled by Customer",
        };
      })
    );
  };

  const loadInitialOrders = async () => {
    setIsOrdersLoading(true);
    try {
      const data = await fetchCancelledByCustomerOrders(0, 10);
      if (data.length === 0 || data.length < 10) {
        setHasMore(false);
      }
      const enrichedOrders = await enrichOrders(data);
      setLoadedOrders(enrichedOrders);
      setCurrentPage(1);
    } catch (err) {
      console.error("Error loading initial orders:", err);
      setError("فشل في تحميل الطلبات. يرجى المحاولة مرة أخرى.");
      toast.error("فشل في تحميل الطلبات!");
    } finally {
      setIsOrdersLoading(false);
    }
  };

  const loadMoreOrders = async () => {
    setIsLoadingMore(true);
    try {
      const data = await fetchCancelledByCustomerOrders(currentPage, 10);
      if (data.length === 0 || data.length < 10) {
        setHasMore(false);
      }
      const enrichedOrders = await enrichOrders(data);
      setLoadedOrders((prev) => [...prev, ...enrichedOrders]);
      setCurrentPage((prev) => prev + 1);
    } catch (err) {
      console.error("Error loading more orders:", err);
      setError("فشل في تحميل المزيد من الطلبات.");
      toast.error("فشل في تحميل المزيد من الطلبات!");
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleUpdateStatus = async (orderId, newStatus, customReason = null) => {
    if (!canAct) return;
    let reason = "no reason";
    
    if (newStatus === "Spam") {
      reason = customReason || "جه ننفذ ولغي";
    } else if (newStatus === "Changes") {
      reason = window.prompt("يرجى إدخال سبب التغيير:");
      if (reason === null) return;
      if (!reason.trim()) {
        toast.error("يجب إدخال سبب التغيير.");
        return;
      }
    }

    const confirmUpdate = window.confirm(`هل أنت متأكد من تحديث حالة الطلب رقم ${orderId} إلى ${newStatus === "Spam" ? "سبام" : newStatus}؟`);
    if (!confirmUpdate) return;

    try {
      await updateOrderStatus(orderId, newStatus, reason);
      setLoadedOrders((prev) => prev.filter((order) => order.orderNumber !== orderId));
      toast.success(`✅ تم تحديث الطلب رقم ${orderId} إلى ${newStatus === "Spam" ? "سبام" : newStatus} بنجاح!`);
    } catch (err) {
      console.error("Error updating order status:", err);
      toast.error("فشل في تحديث حالة الطلب.");
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!canAct) return;
    if (!canMutateOrders(authState.role)) return;
    const confirmDelete = window.confirm(`⚠️ هل أنت متأكد من حذف الطلب رقم ${orderId}؟`);
    if (!confirmDelete) return;
    try {
      await deleteOrder(orderId);
      setLoadedOrders((prev) => prev.filter((order) => order.orderNumber !== orderId));
      toast.success(`🗑️ تم حذف الطلب رقم ${orderId} بنجاح!`);
    } catch (err) {
      console.error("Error deleting order:", err);
      toast.error("فشل في حذف الطلب.");
    }
  };

  // دالة تحويل كل الأوردرات المعروضة إلى سبام دفعة واحدة
  const handleSpamAllOrders = async () => {
    if (loadedOrders.length === 0) {
      toast.warning("لا توجد طلبات لتحويلها إلى سبام!");
      return;
    }

    const confirmSpamAll = window.confirm(
      `⚠️ تحذير: سيتم تحويل ${loadedOrders.length} طلب إلى سبام مع سبب "جه ننفذ ولغي".\n\nهل أنت متأكد من المتابعة؟`
    );
    if (!confirmSpamAll) return;

    setIsSpamAllLoading(true);
    let successCount = 0;
    let failCount = 0;

    // تحويل كل الطلبات إلى سبام بالتتابع
    for (const order of loadedOrders) {
      try {
        await updateOrderStatus(order.orderNumber, "Spam", "جه ننفذ ولغي");
        successCount++;
      } catch (err) {
        console.error(`Failed to update order ${order.orderNumber}:`, err);
        failCount++;
      }
    }

    // إزالة كل الطلبات من القائمة المعروضة
    setLoadedOrders([]);
    setIsSpamAllLoading(false);

    if (successCount > 0) {
      toast.success(
        `✅ تم تحويل ${successCount} طلب إلى سبام بنجاح!${failCount > 0 ? `\n❌ فشل تحويل ${failCount} طلب.` : ''}`,
        { autoClose: 5000 }
      );
    } else if (failCount > 0) {
      toast.error(`❌ فشل تحويل جميع الطلبات (${failCount}).`);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6" dir="rtl">
      <ToastContainer position="top-right" rtl={true} />
      
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">📋 طلبات ملغاة من العميل</h1>
            <p className="text-gray-500 text-sm mt-1">الطلبات التي ألغاها العميل - يمكن تحويلها إلى سبام أو قديم أو تغييرات</p>
          </div>
          <div className="flex gap-3">
            {/* زر تحويل الكل إلى سبام */}
            {loadedOrders.length > 0 && (
              <button
                onClick={handleSpamAllOrders}
                disabled={isSpamAllLoading}
                className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:from-red-600 hover:to-red-700 transition-all shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSpamAllLoading ? (
                  <>
                    <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>جاري التحويل...</span>
                  </>
                ) : (
                  <>
                    <Icons.SpamAll />
                    <span>تحويل الكل إلى سبام</span>
                    <span className="bg-red-700 rounded-full px-2 py-0.5 text-xs mr-1">{loadedOrders.length}</span>
                  </>
                )}
              </button>
            )}
            <button
              onClick={() => navigate("/dashboard")}
              className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span>رجوع</span>
            </button>
          </div>
        </div>

        {/* Stats Summary */}
        {loadedOrders.length > 0 && !isOrdersLoading && (
          <div className="bg-gradient-to-l from-blue-50 to-indigo-50 rounded-xl p-4 mb-6 border border-blue-100">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-3">
                <Icons.CheckCircle />
                <div>
                  <span className="text-sm text-gray-600">عدد الطلبات المعروضة</span>
                  <span className="text-2xl font-bold text-blue-600 block">{loadedOrders.length}</span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Icons.Warning />
                <div>
                  <span className="text-sm text-gray-600">إجمالي المبلغ</span>
                  <span className="text-2xl font-bold text-emerald-600 block">
                    {loadedOrders.reduce((sum, o) => sum + (o.total || 0), 0).toLocaleString()} ج.م
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl mb-6 flex items-center gap-3">
            <Icons.Warning />
            <span>{error}</span>
          </div>
        )}

        {/* Orders Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {isOrdersLoading ? (
            <div className="divide-y divide-gray-100">
              {[...Array(5)].map((_, i) => <SkeletonRow key={i} />)}
            </div>
          ) : (
            <>
              {loadedOrders.length > 0 ? (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">رقم الطلب</th>
                          <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">اسم العميل</th>
                          <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">رقم الهاتف</th>
                          <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">المبلغ</th>
                          <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">الإجراءات</th>
                          <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">حذف</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {loadedOrders.map((order) => (
                          <tr key={order.orderNumber} className="hover:bg-gray-50 transition-colors">
                            <td className="px-4 py-3 text-right font-medium text-gray-900">
                              #{order.orderNumber}
                            </td>
                            <td className="px-4 py-3 text-right text-gray-700">
                              {order.name}
                            </td>
                            <td className="px-4 py-3 text-right text-gray-700 dir-ltr">
                              {order.phoneNumber}{order.phoneNumber2 ? ` - ${order.phoneNumber2}` : ''}
                            </td>
                            <td className="px-4 py-3 text-right font-semibold text-emerald-600">
                              {order.total.toLocaleString()} ج.م
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex flex-wrap gap-2">
                                <button
                                  onClick={() => handleUpdateStatus(order.orderNumber, "Old")}
                                  className="flex items-center gap-1 px-3 py-1.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm"
                                >
                                  <Icons.Old />
                                  <span>قديم</span>
                                </button>
                                <button
                                  onClick={() => handleUpdateStatus(order.orderNumber, "Changes")}
                                  className="flex items-center gap-1 px-3 py-1.5 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors text-sm"
                                >
                                  <Icons.Changes />
                                  <span>تغييرات</span>
                                </button>
                                <button
                                  onClick={() => handleUpdateStatus(order.orderNumber, "Spam")}
                                  className="flex items-center gap-1 px-3 py-1.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-sm"
                                >
                                  <Icons.Spam />
                                  <span>سبام</span>
                                </button>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-center">
                              {(canMutateOrders(authState.role)) && (
                                <button
                                  onClick={() => handleDeleteOrder(order.orderNumber)}
                                  className="flex items-center gap-1 px-3 py-1.5 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors text-sm mx-auto"
                                >
                                  <Icons.Delete />
                                  <span>حذف</span>
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Load More Button */}
                  {hasMore && (
                    <div className="text-center py-4 border-t border-gray-100 bg-gray-50">
                      <button
                        onClick={loadMoreOrders}
                        disabled={isLoadingMore}
                        className="px-6 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 shadow-sm"
                      >
                        {isLoadingMore ? (
                          <span className="flex items-center gap-2">
                            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            جاري التحميل...
                          </span>
                        ) : (
                          "تحميل المزيد"
                        )}
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-12">
                  <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-gray-500 text-lg">لا توجد طلبات ملغاة من العميل</p>
                  <p className="text-gray-400 text-sm mt-1">سيتم عرض الطلبات التي ألغاها العملاء هنا</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CancelledbyCustomerPage;